/**
 * Authentication Helper Functions
 * 
 * Handles token storage and retrieval for HIPAA-compliant session management.
 * Uses sessionStorage for tab isolation (cleared on tab close).
 * Supports app-initiated new tabs via temporary localStorage bridge.
 */

// ============================================================================
// SESSION STORAGE AUTH - HIPAA Compliant
// ============================================================================
// Uses sessionStorage (cleared on tab close) for security
// Supports app-initiated new tabs via temporary localStorage bridge
// ============================================================================

/**
 * Get authentication token
 * Checks sessionStorage first, then localStorage bridge for tab transfers
 */
export const getAuthToken = () => {
  // Primary: sessionStorage (isolated per tab)
  let token = sessionStorage.getItem('token');
  if (token) return token;
  
  // Secondary: Check for app-initiated tab transfer via localStorage
  const transferToken = localStorage.getItem('session_transfer_token');
  const transferTimestamp = localStorage.getItem('session_transfer_timestamp');
  
  if (transferToken && transferTimestamp) {
    const transferTime = parseInt(transferTimestamp);
    const now = Date.now();
    // Only accept transfers within 5 seconds (prevents stale token pickup)
    if (now - transferTime < 5000) {
      void 0 && console.log('[AUTH] Received session transfer from parent tab');
      // Move to sessionStorage and clear the transfer
      sessionStorage.setItem('token', transferToken);
      localStorage.removeItem('session_transfer_token');
      localStorage.removeItem('session_transfer_timestamp');
      return transferToken;
    } else {
      // Expired transfer, clean up
      localStorage.removeItem('session_transfer_token');
      localStorage.removeItem('session_transfer_timestamp');
    }
  }
  
  return null;
};

/**
 * Set authentication token (always in sessionStorage)
 */
export const setAuthToken = (token) => {
  sessionStorage.setItem('token', token);
};

/**
 * Clear authentication token and related data
 */
export const clearAuthToken = () => {
  sessionStorage.removeItem('token');
  localStorage.removeItem('session_transfer_token');
  localStorage.removeItem('session_transfer_timestamp');
};

/**
 * Open a new tab with session transfer
 * Use this instead of window.open when you need the new tab to be authenticated
 */
export const openAuthenticatedTab = (url) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    // Temporarily store in localStorage for the new tab to pick up
    localStorage.setItem('session_transfer_token', token);
    localStorage.setItem('session_transfer_timestamp', Date.now().toString());
  }
  return window.open(url, '_blank');
};

// Export for use globally (some components use window.openAuthenticatedTab)
if (typeof window !== 'undefined') {
  window.openAuthenticatedTab = openAuthenticatedTab;
}

export default {
  getAuthToken,
  setAuthToken,
  clearAuthToken,
  openAuthenticatedTab
};
